import type { Snapshot, Gate, Lane } from "../types";
import { STAGE_ORDER, STAGE_LABEL, laneBrand } from "../lanes";

export function VeyraTab({ snap, onOpenGate }: { snap: Snapshot | null; onOpenGate: (g: Gate) => void }) {
  const buckets = new Map<string, Gate[]>();
  for (const s of STAGE_ORDER) buckets.set(s, []);
  const gates = snap?.veyra.gates ?? [];
  for (const g of gates) {
    if (!buckets.has(g.stage)) buckets.set(g.stage, []);
    buckets.get(g.stage)!.push(g);
  }
  const veyraLanes = (snap?.fleet.lanes ?? []).filter((l) => laneBrand(l.id) === "veyrapay");
  return (
    <div className="space-y-5">
      {!snap && <SoftEmptyBanner brand="VeyraPay" />}
      <Tiles snap={snap} />
      <BrandLaneStrip lanes={veyraLanes} />
      <Kanban buckets={buckets} onOpenGate={onOpenGate} />
    </div>
  );
}

function SoftEmptyBanner({ brand }: { brand: string }) {
  return (
    <div className="bg-panel border border-edge rounded-md p-3 text-xs text-mist">
      Aggregator not ready · showing soft-empty {brand} view. Panels fill in when the feed reconnects.
    </div>
  );
}

function Kanban({ buckets, onOpenGate }: { buckets: Map<string, Gate[]>; onOpenGate: (g: Gate) => void }) {
  return (
    <div className="grid grid-cols-5 gap-3">
      {STAGE_ORDER.map((stage) => {
        const items = buckets.get(stage) ?? [];
        return (
          <div key={stage} className="bg-panel border border-edge rounded-md min-h-[280px] flex flex-col">
            <div className="px-3 py-2 border-b border-edge text-xs uppercase tracking-wide flex items-center justify-between">
              <span>{STAGE_LABEL[stage]}</span>
              <span className="text-mist">{items.length}</span>
            </div>
            <div className="p-2 space-y-2 overflow-auto scroll-thin flex-1">
              {items.length === 0 && (
                <div className="text-mist text-xs px-1">Empty</div>
              )}
              {items.map((g) => <GateCard key={g.slug} gate={g} onClick={() => onOpenGate(g)} />)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function GateCard({ gate, onClick }: { gate: Gate; onClick: () => void }) {
  const stageColor: Record<string, string> = {
    knox: "bg-mist", sage: "bg-glow", rex: "bg-warn", ash: "bg-good", live: "bg-good", dead: "bg-bad",
  };
  return (
    <button
      onClick={onClick}
      className="w-full text-left bg-ink/60 border border-edge rounded hover:border-mist transition overflow-hidden"
    >
      <div className={`h-0.5 ${stageColor[gate.stage] ?? "bg-mist"}`} />
      <div className="p-2">
        <div className="text-sm font-mono truncate">{gate.slug}</div>
        <div className="text-xs text-mist flex justify-between mt-1">
          <span>{gate.owner_lane || "—"}</span>
          <span>{relTs(gate.last_event_ts)}</span>
        </div>
      </div>
    </button>
  );
}

function Tiles({ snap }: { snap: Snapshot | null }) {
  const tiles = [
    {
      label: "Shield live",
      value: snap?.veyra.shield.live_count ?? "—",
      sub: snap ? `${snap.veyra.shield.dead_count} dead` : "no data",
    },
    {
      label: "Today throughput",
      value: snap ? `$${Math.round(snap.veyra.forecast_today_usd).toLocaleString()}` : "—",
      sub: "forecast",
    },
    {
      label: "Pool parity",
      value: snap?.veyra.shield.pool_parity ?? "—",
      sub: snap ? (snap.veyra.shield.pool_parity === "ok" ? "all good" : "needs sync") : "no data",
    },
    {
      label: "Stuck gates",
      value: snap?.veyra.stuck_gates.length ?? "—",
      sub: snap ? stuckSummary(snap) : "no data",
    },
  ];
  return (
    <div className="grid grid-cols-4 gap-3">
      {tiles.map((t) => (
        <div key={t.label} className="bg-panel border border-edge rounded-md p-3">
          <div className="text-mist text-xs uppercase tracking-wide">{t.label}</div>
          <div className="text-2xl mt-1">{t.value}</div>
          <div className="text-mist text-xs mt-0.5">{t.sub}</div>
        </div>
      ))}
    </div>
  );
}

function BrandLaneStrip({ lanes }: { lanes: Lane[] }) {
  if (lanes.length === 0) {
    return (
      <div className="bg-panel border border-edge rounded-md p-3 text-xs text-mist">
        VeyraPay desks: Ash · Ralph · Vex · Forge — no live state yet.
      </div>
    );
  }
  return (
    <div className="bg-panel border border-edge rounded-md p-3">
      <div className="text-mist text-xs uppercase tracking-wide mb-2">VeyraPay desks</div>
      <div className="grid grid-cols-4 gap-2">
        {lanes.map((l) => (
          <div key={l.id} className="bg-ink/60 border border-edge rounded p-2 text-xs">
            <div className="flex items-center gap-1">
              <span>{l.emoji}</span>
              <span>{l.name}</span>
            </div>
            <div className="text-mist mt-0.5">{l.status}</div>
            <div className="text-mist truncate">{l.last_action_summary || "—"}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function stuckSummary(snap: Snapshot): string {
  if (snap.veyra.stuck_gates.length === 0) return "all moving";
  const top = snap.veyra.stuck_gates[0];
  return `${top.slug} · ${Math.round(top.stage_age_hours)}h`;
}

function relTs(ts: string) {
  try {
    const mins = Math.round((Date.now() - new Date(ts).getTime()) / 60_000);
    if (mins < 1) return "now";
    if (mins < 60) return `${mins}m`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    return `${Math.round(hrs / 24)}d`;
  } catch { return "—"; }
}
